import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, message, Segmented, Skeleton, Tag, Tooltip, Typography } from 'antd';
import styled from 'styled-components';

import { CopyOutlined } from '@ant-design/icons';

import {
  formatParsedCvExtractedAt,
  hasParsedCvProvenanceMismatch,
  mapCandidateAiParsedCvError,
  parsedCvProvenanceMismatchError,
} from '../candidate-ai/candidateAiParsedCv';
import { CandidateAiParsedCvMarkdown } from '../candidate-ai/CandidateAiParsedCvMarkdown';
import { buildCandidateAiParsedCvQueryKey, useGetCandidateAiParsedCv } from '../candidate-ai/useGetCandidateAiParsedCv';
import { CandidateType } from '../candidates/Candidates.types';

import { SuggestionIconButton } from './CandidateAiScalarRows';

const PanelWrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Same height as a suggestion section heading, so both panes start on one line.
const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 32px;
`;

const PanelTitleCluster = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 13px;
`;

const ViewToggle = styled(Segmented)`
  &.ant-segmented {
    padding: 2px;
    background: #eef2f6;
    border-radius: 6px;
  }
  .ant-segmented-item-label {
    min-height: 26px;
    line-height: 26px;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 500;
    color: #526174;
  }
  .ant-segmented-item-selected .ant-segmented-item-label {
    color: #18283b;
  }
`;

const PanelTitle = styled(Typography.Text)`
  &.ant-typography {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #526174;
  }
`;

const PanelControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #edf2f8;
  border-radius: 12px;
  padding: 16px 18px;
`;

const SourceBlock = styled.pre`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
`;

const emptyDocumentMessage = 'No parsed CV text yet. It becomes available once the CV is read.';

const previousCvMessage = 'This text was extracted from an earlier CV, not the one currently attached.';

const copySuccessMessage = 'Markdown copied';
const copyFailureMessage = 'Could not copy to clipboard';

type ParsedCvView = 'preview' | 'source';

type CandidateAiParsedCvPanelProps = {
  candidate: CandidateType;
  open: boolean;
};

export const CandidateAiParsedCvPanel: React.FC<CandidateAiParsedCvPanelProps> = ({ candidate, open }) => {
  const [view, setView] = useState<ParsedCvView>('preview');

  const candidateId = candidate.uuid;
  const currentCvUuid = candidate.cv?.uuid ?? null;
  const cvProcessing = candidate.cvProcessing;

  const { data, error, isError, isFetching, isLoading, refetch } = useGetCandidateAiParsedCv({
    candidateId,
    currentCvUuid,
    cvProcessing,
    enabled: open,
  });

  const identity = useMemo(
    () => buildCandidateAiParsedCvQueryKey({ candidateId, currentCvUuid, cvProcessing }).join('|'),
    [candidateId, currentCvUuid, cvProcessing]
  );
  const mismatchRefetchIdentity = useRef<string | null>(null);

  const isMismatched = hasParsedCvProvenanceMismatch(candidate.aiSuggestions, data);

  // Refetch once per identity, so a mismatch that survives it is surfaced rather than looped on.
  useEffect(() => {
    if (!isMismatched || isFetching || mismatchRefetchIdentity.current === identity) {
      return;
    }

    mismatchRefetchIdentity.current = identity;
    refetch();
  }, [identity, isFetching, isMismatched, refetch]);

  const handleCopy = useCallback(async () => {
    if (!data) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.markdown);
      message.success(copySuccessMessage);
    } catch {
      message.error(copyFailureMessage);
    }
  }, [data]);

  const isMismatchResolving = isMismatched && (isFetching || mismatchRefetchIdentity.current !== identity);
  const isMismatchUnresolved = isMismatched && !isFetching && mismatchRefetchIdentity.current === identity;
  const panelError = isMismatchUnresolved
    ? parsedCvProvenanceMismatchError
    : isError && error
    ? mapCandidateAiParsedCvError(error)
    : null;

  const hasDocument = !!data && !isMismatched;

  const renderBody = () => {
    if (isLoading) {
      return <Skeleton active title={false} paragraph={{ rows: 8 }} />;
    }

    if (panelError) {
      if (panelError.kind === 'candidate-unavailable') {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={panelError.message} />;
      }

      return (
        <Alert
          showIcon
          type="error"
          message={panelError.message}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      );
    }

    if (isMismatchResolving) {
      return <Typography.Text type="secondary">Updating parsed CV...</Typography.Text>;
    }

    if (!data) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDocumentMessage} />;
    }

    if (view === 'source') {
      return <SourceBlock data-testid="parsed-cv-source">{data.markdown}</SourceBlock>;
    }

    return <CandidateAiParsedCvMarkdown markdown={data.markdown} />;
  };

  const extractedAt = formatParsedCvExtractedAt(data?.parsedAt);

  return (
    <PanelWrap aria-label="Parsed CV">
      <PanelHeader>
        <PanelTitleRow>
          <PanelTitleCluster>
            <PanelTitle>Parsed CV</PanelTitle>
            {hasDocument && !!extractedAt && (
              <Typography.Text type="secondary">{`Extracted ${extractedAt}`}</Typography.Text>
            )}
          </PanelTitleCluster>
          <PanelControls>
            <ViewToggle
              value={view}
              disabled={!hasDocument}
              onChange={(value) => setView(value as ParsedCvView)}
              options={[
                { label: 'Preview', value: 'preview' },
                { label: 'Source', value: 'source' },
              ]}
            />
            <Tooltip title="Copy markdown">
              <SuggestionIconButton
                $tone="neutral"
                type="text"
                aria-label="Copy markdown"
                icon={<CopyOutlined />}
                disabled={!hasDocument}
                onClick={handleCopy}
              />
            </Tooltip>
          </PanelControls>
        </PanelTitleRow>
        {hasDocument && data && (
          <>
            {!data.basedOnCurrentCv && (
              <PanelControls>
                <Tag color="warning">From a previous CV</Tag>
                <Typography.Text type="secondary">{previousCvMessage}</Typography.Text>
              </PanelControls>
            )}
          </>
        )}
      </PanelHeader>
      <PanelBody>{renderBody()}</PanelBody>
    </PanelWrap>
  );
};
